import { reflect } from '@effector/reflect';
import { NumberInput } from '@mantine/core';
import { combine } from 'effector';
import { useUnit } from 'effector-react';
import { Coins } from 'lucide-react';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { viewerModel } from 'entities/viewer';
import { WalletWithAccountId } from 'shared/api';
import { translated, $t } from 'shared/lib';
import { createModalForm, ModalFormButtonProps, ModalFormProps } from 'shared/ui';
import { DepositWalletModel } from '../../model';

export const createDepositWalletModal = (model: DepositWalletModel) => {
	const ModalForm = createModalForm(model);

	const AmountField = reflect({
		view: NumberInput,
		bind: {
			'data-test-id': 'deposit-wallet-amount-input',
			value: model.form.fields.amount.$value,
			onChange: (e) => model.form.fields.amount.change(`${e}`),
			label: translated('deposit-wallet.amount'),
			placeholder: '250.00',
			rightSection: model.$modalData.map((wallet) => wallet?.currency ?? null),
			errorProps: { 'data-test-id': 'deposit-wallet-amount-input-error' },
			error: combine(
				[model.form.fields.amount.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('deposit-wallet.amount'),
					}),
			),
		},
	});

	const DepositWalletModal = forwardRef<HTMLDivElement, ModalFormProps>(({ ...props }, ref) => {
		const { t } = useTranslation();

		const pending = useUnit(model.$pending);

		return (
			<ModalForm
				ref={ref}
				title={t('deposit-wallet.title')}
				submitButtonProps={{
					children: t('deposit-wallet.deposit'),
					loading: pending,
				}}
				cancelButtonProps={{
					children: t('deposit-wallet.cancel'),
				}}
				{...props}
			>
				<AmountField />
			</ModalForm>
		);
	});

	const DepositWalletModalButton = forwardRef<
		HTMLButtonElement,
		ModalFormButtonProps<WalletWithAccountId>
	>(({ modalData, ...props }, ref) => {
		const { t } = useTranslation();
		const userAccountId = useUnit(viewerModel.$accountId);

		return (
			<ModalForm.Button
				ref={ref}
				data-test-id='deposit-wallet-button'
				leftSection={<Coins />}
				disabled={modalData.isLocked || modalData.accountId === userAccountId}
				modalData={modalData}
				{...props}
			>
				{t('deposit-wallet.deposit')}
			</ModalForm.Button>
		);
	});

	return Object.assign(DepositWalletModal, {
		Button: DepositWalletModalButton,
	});
};
